import { useShowDebugItems } from "@/hooks/useShowDebugItems";
import type { LineItem, MappedTuple, Tuple } from "@/interfaces/estimate-detail";
import { isItemArchived } from "@rebuild/shared";
import type { Estimation } from "@/interfaces/estimation.interface";
import { Sparkle } from "lucide-react";
import type { ReactNode } from "react";
import type { Category } from ".";
import { Line } from "./Line";
import { getConfidence } from "./util/get-confidence";

interface GroupLinesProps {
  itemIcon?: Record<string, ReactNode>;
  selectedTab?: Category;
  lines: MappedTuple[];
  tuples: Tuple[];
  pageId: string;
  onDelete?: (val: string, uniqueId: string, category?: Category) => void;
  onSelectItem?: (uniqueId: string, category?: Category) => void;
  lineClassName?: string;
  className?: string;
  showConfirm?: boolean;
  estimation?: Estimation;
  estimateId?: string;
}

export const GroupLines = ({
  tuples,
  lines,
  itemIcon,
  selectedTab,
  pageId,
  onSelectItem,
  onDelete,
  lineClassName,
  className,
  showConfirm,
  estimateId,
}: GroupLinesProps) => {
  const isDebugItemsEnabled = useShowDebugItems();

  const allLineItemsByUniqueId = tuples.reduce(
    (acc, tuple) => {
      for (const li of tuple.lineItems) {
        acc[li.uniqueId] = li;
      }
      return acc;
    },
    {} as Record<string, LineItem>,
  );

  const linesGroupedByComplementOfId = lines.reduce(
    (acc, line) => {
      const directParentUniqueId = line.currentItem.parentLineItemUuid;
      const parentTupleId = line.tuple.isComplementOf;

      // When the parent line item is itself shown in this tab (e.g. an issue with a downgraded recommendation), group
      // the recommendation under the parent's own tuple so the two render together instead of in separate groups.
      const parentTupleShownHere = !!parentTupleId && lines.some((otherLine) => otherLine.tuple.tupleId === parentTupleId);
      const key = parentTupleShownHere ? parentTupleId : directParentUniqueId || parentTupleId || line.tuple.tupleId;

      if (!acc[key]) {
        acc[key] = { mainTuple: "", lines: [], mainLineItemOrderIndex: 9999, hasParentLineItem: false };
      }

      acc[key].lines.push(line);

      // The parent line item of the group renders directly, so don't also show a redundant header for it.
      const isParentLineItemOfGroup = line.tuple.tupleId === key && !parentTupleId;

      if (isParentLineItemOfGroup) {
        acc[key].hasParentLineItem = true;
        acc[key].mainLineItemOrderIndex = line.currentItem.orderIndex ?? acc[key].mainLineItemOrderIndex;
      } else if (directParentUniqueId) {
        const directParentItem = allLineItemsByUniqueId[directParentUniqueId];
        acc[key].mainTuple = directParentItem?.lineItem ?? "";
        acc[key].mainLineItemOrderIndex = directParentItem?.orderIndex ?? 9999;
      } else {
        const mainTuple = tuples.find((tuple) => tuple.tupleId === line.tuple.isComplementOf);
        const mainLineItem = mainTuple?.lineItems.find((lineItem) => !lineItem.isComplement);
        acc[key].mainTuple = mainTuple ? (mainTuple?.lineItems[0]?.lineItem ?? "") : "";
        acc[key].mainLineItemOrderIndex = mainLineItem?.orderIndex !== undefined ? mainLineItem.orderIndex : 9999;
      }

      return acc;
    },
    {} as Record<string, { mainTuple: string; mainLineItemOrderIndex: number; lines: MappedTuple[]; hasParentLineItem: boolean }>,
  );

  const lineGroupsSorted = Object.values(linesGroupedByComplementOfId).sort(
    (groupA, groupB) => groupA.mainLineItemOrderIndex - groupB.mainLineItemOrderIndex,
  );

  let increment = 1;
  return (
    <div>
      {lineGroupsSorted.map((group, groupIndex) => {
        return (
          <div
            key={group.lines.map((line) => line.tuple.tupleId).join("__")}
            className={`border-[#EEEFF1] ${selectedTab === "recommended" ? "p-[2px]" : ""} ${className}`}
          >
            {!group.hasParentLineItem && group.mainTuple?.length > 1 && (
              <p className={`w-full bg-white px-4 py-2 text-sm text-[#71717A] ${groupIndex === 0 ? "rounded-t-xl" : ""}`}>
                {group.mainTuple}
              </p>
            )}
            {[...group.lines]
              // Render the parent line item before its recommendations so recommendations sit beneath their parent.
              .sort((lineA, lineB) => Number(!!lineA.tuple.isLikely) - Number(!!lineB.tuple.isLikely))
              .map(({ tuple, currentItem }, index) => {
                const confidence = getConfidence(currentItem, tuple);

                const order = selectedTab === "recommended" ? increment : groupIndex + 1;

                increment++;

                // Add light red background for deleted items when debug mode is enabled
                const isDeletedItem = isItemArchived(tuple) || isItemArchived(currentItem);
                const deletedItemClass = isDeletedItem && isDebugItemsEnabled ? "bg-red-50" : "bg-white";
                const baseClasses = `${deletedItemClass} ${index === 0 && groupIndex === 0 && !(!group.hasParentLineItem && group.mainTuple?.length > 1) ? "rounded-t-xl" : ""} ${lineClassName}`;

                // On the issues tab, a recommendation that lives under an issue should show the recommendation icon in
                // addition to the issue icon so it's clear which issues are recommendations and which aren't.
                const isRecommendation = !!tuple.isLikely;
                const tabIcon = itemIcon?.[selectedTab ?? ""];
                const icon =
                  selectedTab === "issues" && isRecommendation ? (
                    <span className="flex items-center gap-1">
                      {tabIcon}
                      <Sparkle className="h-5 w-5 text-blue-600" />
                    </span>
                  ) : (
                    tabIcon
                  );
                return (
                  <Line
                    order={order}
                    isFreeFormFlag={tuple.isFreeFormFlag ?? false}
                    estimateId={estimateId}
                    lineItem={currentItem}
                    tuple={tuple}
                    showConfirm={showConfirm}
                    selectedTab={selectedTab}
                    confidence={confidence}
                    icon={icon}
                    pageId={pageId}
                    className={baseClasses}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(tuple.tupleId, currentItem.uniqueId, selectedTab);
                    }}
                    key={`${currentItem.lineItem}_${index}_${tuple.tupleId}`}
                    onSelect={() => onSelectItem?.(currentItem.uniqueId, selectedTab)}
                    tupleId={tuple.tupleId}
                  />
                );
              })}
          </div>
        );
      })}
    </div>
  );
};
